'use client'

import type { CreatePortfolioSchema } from '@labi-api-libs/schemas'
import { useMutation } from '@tanstack/react-query'
import Cookies from 'js-cookie'
import { SquareArrowOutUpRight } from 'lucide-react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ExternalMenu } from '@/app/(app)/menu/components/ExternalMenu'
import { ModuleMenu } from '@/app/(app)/menu/components/ModuleMenu'
import {
  ScrollArea,
  Sidebar,
  SidebarFooter,
  SidebarHeader,
} from '@/components/ui'
import { Skeleton } from '@/components/ui/skeleton'
import { useConfig } from '@/contexts/configProvider'
import { useAppearance } from '@/hooks/queries/configs'
import { useGetPortfolios } from '@/hooks/queries/project'
import { useGetMe } from '@/hooks/queries/users/useGetMe'
import { cn } from '@/lib/cn'
import { projectService } from '@/services/http/projectService'
import type { Portfolio } from '@/services/http/projectService/portfolio'
import type { User } from '@/services/http/usersService/types'
import { Actors } from '@/types/actors'
import { USER_INFO_COOKIE } from '@/utils/constants'
import { generateRoutePath } from '@/utils/routes/generateRoutePath'
import {
  MENU_EXTERNAL_ITEMS,
  MENU_MODULES,
  type MenuItem,
} from '@/utils/routes/menu'

function MenuSkeleton({ isCollapsed }: { isCollapsed: boolean }) {
  return (
    <div
      className={cn(
        'flex flex-col h-full p-4 gap-2',
        isCollapsed ? 'min-w-[76px]' : 'min-w-[268px]',
      )}
    >
      <Skeleton className="mb-6 h-10 w-3/4" />
      {[...Array(6)].map((_, i) => (
        <Skeleton key={i} className="mb-3 h-8 w-full" />
      ))}
      <div className="mt-auto">
        <Skeleton className="h-8 w-1/2" />
      </div>
    </div>
  )
}

export const Menu = () => {
  const pathname = usePathname()
  const { push } = useRouter()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [activeMenu, setActiveMenu] = useState('')
  const [user, setUser] = useState<User | null>(null)
  const [isOpenDialogCreatePortfolio, setIsOpenDialogCreatePortfolio] =
    useState(false)

  const { appearance, isLoading } = useAppearance()
  const { config } = useConfig()
  const { user: userFromQuery, isLoading: isLoadingUser } = useGetMe()
  // `=== true` de propósito: no TanStack Query v5 `enabled: undefined` vale
  // como `true`, e enquanto a config do tenant carrega `config` é `null`.
  const { portfolios, refetchPortfolio } = useGetPortfolios({
    enabled: config?.hasProjectModule === true,
  })
  const [portfolioMenu, setPortfolioMenu] = useState<MenuItem[]>(() => {
    const projectsMenu = MENU_MODULES.find((menu) => menu.key === 'projects')
    return projectsMenu ? [projectsMenu] : []
  })

  const updatePortfolioMenu = useCallback((portfolios: Portfolio[]) => {
    const projectsMenu = MENU_MODULES.find((menu) => menu.key === 'projects')

    if (projectsMenu) {
      setPortfolioMenu([
        {
          ...projectsMenu,
          items: portfolios.map((portfolio) => ({
            icon: null,
            label: portfolio.title,
            link: generateRoutePath('projects.portfolio', {
              params: { id: portfolio.id },
              query: { tab: 'board' },
            }),
            matchPattern: `/projects/portfolio/${portfolio.id}`,
            items: [],
            key: portfolio.id,
            subItems: [],
            allowedBy: [Actors.IDEATOR, Actors.MANAGER],
            disabled: false,
          })),
        },
      ])
    }
  }, [])

  const { mutate: createPortfolio, isPending } = useMutation({
    mutationKey: ['createPortfolio'],
    mutationFn: (data: CreatePortfolioSchema) =>
      projectService.portfolio.create(data),
    onSuccess: (data) => {
      refetchPortfolio()
      push(
        generateRoutePath('projects.portfolio', {
          params: { id: data.id },
          query: { tab: 'board' },
        }),
      )
      setActiveMenu('projects')
    },
  })

  const handleCreatePortfolio = useCallback(
    (data: CreatePortfolioSchema) => {
      createPortfolio(data)
    },
    [createPortfolio],
  )

  useEffect(() => {
    if (portfolios.length > 0 && config?.hasProjectModule) {
      updatePortfolioMenu(portfolios)
    }
  }, [config?.hasProjectModule, portfolios, updatePortfolioMenu])

  const modulesByActor = useMemo(() => {
    if (!user) return []

    const modules = MENU_MODULES.filter(
      (menuItem) => menuItem.key !== 'projects',
    )
      .filter((menuItem) => {
        if (menuItem.allowedBy) {
          return menuItem.allowedBy.includes(user.role)
        }
        return true
      })
      .map((menuItem) => ({
        ...menuItem,
        items: menuItem.items.filter((item) =>
          item.allowedBy.includes(user.role),
        ),
      }))
      .filter((menuItem) => {
        return menuItem.items.length > 0
      })

    if (!config?.hasOpenInnovationModule) {
      return modules.filter((menuItem) => menuItem.key !== 'open-innovation')
    }

    return modules
  }, [config?.hasOpenInnovationModule, user])

  const isExternalMenuSelected = useMemo(() => {
    return MENU_EXTERNAL_ITEMS.some((item) => item.key === activeMenu)
  }, [activeMenu])

  const selectedMenu = useMemo(() => {
    if (isExternalMenuSelected) {
      return MENU_EXTERNAL_ITEMS.find((item) => item.key === activeMenu)
    }

    return modulesByActor.find((item) => item.key === activeMenu)
  }, [activeMenu, isExternalMenuSelected, modulesByActor])

  useEffect(() => {
    const foundMenuByPathname = [
      ...modulesByActor,
      ...MENU_EXTERNAL_ITEMS,
    ].find((menuItem) => {
      return menuItem.items?.some((item) => {
        if (item.link === pathname) return true
        return item.subItems?.some((subItem) => subItem.link === pathname)
      })
    })

    if (foundMenuByPathname) {
      setActiveMenu(foundMenuByPathname.key)
    }
  }, [modulesByActor, pathname])

  useEffect(() => {
    if (userFromQuery) {
      setUser(userFromQuery)
      Cookies.set(USER_INFO_COOKIE, JSON.stringify(userFromQuery))
    }
  }, [userFromQuery])

  useEffect(() => {
    if (pathname.includes('projects/portfolio')) {
      setActiveMenu('projects')
    }
  }, [pathname])

  const hasPermissionToCreatePortfolio = useMemo(() => {
    return user?.isAdmin || user?.isProjectAdmin || false
  }, [user])

  const isMenuLoading = isLoading || isLoadingUser

  return (
    <Sidebar
      isCollapsed={isCollapsed}
      className={cn(
        'shadow-md [grid-area:sidebar] z-10',
        isCollapsed ? 'min-w-[76px]' : 'min-w-[268px]',
      )}
    >
      <SidebarHeader
        onCollapse={() => setIsCollapsed((prevState) => !prevState)}
        logoUrl={
          (isCollapsed ? appearance?.logo : appearance?.bigLogo) ??
          '/images/logos/avantti-purple.png'
        }
        isLoadingLogoUrl={isLoading}
        altText="Header Company Logo"
      />
      <ScrollArea className="h-full">
        {isMenuLoading ? (
          <MenuSkeleton isCollapsed={isCollapsed} />
        ) : isExternalMenuSelected ? (
          <ExternalMenu
            activeMenu={activeMenu}
            isCollapsed={isCollapsed}
            pathname={pathname}
            selectedMenu={selectedMenu}
            user={user}
          />
        ) : (
          <ModuleMenu
            activeMenu={activeMenu}
            isCollapsed={isCollapsed}
            modulesByActor={modulesByActor}
            pathname={pathname}
            selectedMenu={selectedMenu}
            setActiveMenu={setActiveMenu}
            portfolioMenu={portfolioMenu}
            hasPermissionToCreatePortfolio={hasPermissionToCreatePortfolio}
            isOpenDialogCreatePortfolio={isOpenDialogCreatePortfolio}
            setIsOpenDialogCreatePortfolio={setIsOpenDialogCreatePortfolio}
            handleCreatePortfolio={handleCreatePortfolio}
            isPending={isPending}
            hasProjectModule={config?.hasProjectModule || false}
          />
        )}
      </ScrollArea>

      {!isMenuLoading && (
        <div className="px-3 py-2">
          {MENU_EXTERNAL_ITEMS.map(
            ({ icon: Icon, key, label, link, isAdmin, allowedEmails }) => {
              if (isAdmin && !user?.isAdmin) return null
              if (
                allowedEmails &&
                user?.email &&
                !allowedEmails.includes(user.email)
              )
                return null
              return (
                <Link
                  key={key}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2"
                  href={generateRoutePath(link)}
                >
                  {isCollapsed ? (
                    <Icon className="size-5 text-system-text-secondary md:size-6" />
                  ) : (
                    <>
                      <Icon className="size-5 text-system-text-secondary md:size-6" />
                      <span className="text-sm font-semibold text-system-text-secondary">
                        {label}
                      </span>
                      <SquareArrowOutUpRight className="size-4 text-system-text-secondary" />
                    </>
                  )}
                </Link>
              )
            },
          )}
        </div>
      )}

      <SidebarFooter
        altText="Footer Company Logo"
        logoUrl={
          isCollapsed
            ? '/images/logos/Avantti.png'
            : '/images/logos/avantti-grey.png'
        }
      />
    </Sidebar>
  )
}

Menu.displayName = 'Menu'
